import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { getLotShapeLabel } from '../../../utils/lotStreetDisplay';
import { lotInfoRowOuterSx, LOT_INFO_ROW_INNER_SX, lotInfoFieldLabelLayoutSx, lotInfoFieldValueSx } from './formBlockUi';

// ─── Constantes ──────────────────────────────────────────────────────────────
const EARTH_RADIUS_M  = 6371008.8;
const MAX_VISIBLE_LABELS = 14;

const STROKE_COLOR = 'rgba(255,255,255,0.85)';
const FILL_COLOR   = 'rgba(96,165,250,0.10)';
const LABEL_COLOR  = '#e2e8f0';
const GRID_COLOR   = 'rgba(255,255,255,0.05)';

const SECTION_TITLE_SX = {
  color: 'rgba(255,255,255,0.40)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 600,
};

// ─── Helpers ───────────────────────────────────
const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

const haversineMeters = (a, b) => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
};

const bearingDeg = (a, b) => {
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
};

const formatBearingLabel = (deg) => {
  if (!Number.isFinite(deg)) return '';
  const azim = ((deg % 360) + 360) % 360;
  const [baseNS, baseEW, angle] =
    azim <= 90  ? ['N', 'E', azim]       :
    azim <= 180 ? ['S', 'E', 180 - azim] :
    azim <= 270 ? ['S', 'O', azim - 180] :
                  ['N', 'O', 360 - azim];
  return `${baseNS} ${angle.toFixed(0)}° ${baseEW}`;
};

const angularDiffDeg = (a, b) => {
  let diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
};

// TODO: reactivar cuando el marcado de frente por azimuth esté pulido.
// const isFrontSide = (groupAzimuth, frontAzimuth, tolerance = 10) => {
//   if (!Number.isFinite(groupAzimuth) || !Number.isFinite(frontAzimuth)) return false;
//   const diff = Math.abs(((groupAzimuth - frontAzimuth) + 360) % 360);
//   return diff <= tolerance || diff >= 360 - tolerance;
// };

const formatMetersAR = (value, decimals = 2) => {
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};



const niceScaleStep = (targetMeters) => {
  if (!Number.isFinite(targetMeters) || targetMeters <= 0) return 1;
  const exp  = Math.floor(Math.log10(targetMeters));
  const frac = targetMeters / 10 ** exp;
  const nice = frac < 1.5 ? 1 : frac < 3.5 ? 2 : frac < 7.5 ? 5 : 10;
  return nice * 10 ** exp;
};

const perpendicularDistanceToSegment = (point, lineStart, lineEnd) => {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
  const t = Math.max(
    0,
    Math.min(1, ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lenSq),
  );
  return Math.hypot(
    point.x - (lineStart.x + t * dx),
    point.y - (lineStart.y + t * dy),
  );
};

// ─── Proyección ───────────────────────────────────────────────────────────────

/**
 * MEJORA: acepta `origin` precalculado para evitar recalcular el centroide
 * cuando se llama múltiples veces sobre variantes del mismo boundary.
 */
const projectToLocalMeters = (pts, origin = null) => {
  if (pts.length === 0) return { points: [], origin: null };
  const o = origin ?? pts.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat / pts.length, lng: acc.lng + p.lng / pts.length }),
    { lat: 0, lng: 0 },
  );
  const cosLat = Math.cos(toRad(o.lat));
  const points = pts.map((p) => ({
    x: toRad(p.lng - o.lng) * EARTH_RADIUS_M * cosLat,
    y: toRad(p.lat - o.lat) * EARTH_RADIUS_M,
  }));
  return { points, origin: o };
};

// ─── Geometría del polígono ────────────────────────────────────────────────────

const computePolygonMetrics = (pts, segments) => {
  let shoelace = 0;
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let perimeterM = 0;

  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    shoelace += a.x * b.y - b.x * a.y;
    if (a.x < minX) minX = a.x;
    if (a.x > maxX) maxX = a.x;
    if (a.y < minY) minY = a.y;
    if (a.y > maxY) maxY = a.y;
    perimeterM += segments[i].lengthM;
  }

  const signedArea = shoelace / 2;
  return {
    areaM2:     Math.abs(signedArea),
    isCCW:      signedArea > 0,
    perimeterM,
    bbox: {
      minX, maxX, minY, maxY,
      widthM:  Math.max(maxX - minX, 0.0001),
      heightM: Math.max(maxY - minY, 0.0001),
    },
  };
};

const buildSegmentsFromBoundary = (boundary, points) =>
  points.map((p, i) => {
    const a = boundary[i];
    const b = boundary[(i + 1) % boundary.length];
    return {
      index:   i,
      start:   p,
      end:     points[(i + 1) % points.length],
      lengthM: haversineMeters(a, b),
      azimuth: bearingDeg(a, b),
    };
  });

const getCollinearToleranceDeg = (vertexCount, perimeterM) => {
  let t = vertexCount > 80 ? 15 : vertexCount > 50 ? 12 : vertexCount > 24 ? 10 : vertexCount > 12 ? 8 : 6;
  if (perimeterM > 5000) t += 4;
  else if (perimeterM > 2000) t += 2;
  return Math.min(t, 18);
};

const getTargetMaxSides = (vertexCount, perimeterM) => {
  if (vertexCount <= 10) return vertexCount;
  if (perimeterM > 4000) return 10;
  if (perimeterM > 1500) return 12;
  if (vertexCount > 60)  return 12;
  if (vertexCount > 30)  return 14;
  return 16;
};

const getInitialSimplifyEpsilon = (vertexCount, perimeterM) => {
  let e = Math.max(0.4, perimeterM / 2500);
  if (vertexCount > 80) e *= 1.5;
  else if (vertexCount > 40) e *= 1.25;
  if (perimeterM > 3000) e *= 1.35;
  return e;
};

const groupCollinearSegments = (segments, points, toleranceDeg) => {
  if (segments.length === 0) return [];
  const n = segments.length;

  const buildGroup = (startIdx, endIdx) => {
    const startPoint = points[startIdx];
    const endPoint   = points[(endIdx + 1) % n];
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const chordLen = Math.hypot(dx, dy) || 1;

    let lengthM = 0;
    let count   = 0;
    let idx     = startIdx;
    while (true) {
      lengthM += segments[idx].lengthM;
      count   += 1;
      if (idx === endIdx) break;
      idx = (idx + 1) % n;
    }

    return {
      startIndex:    startIdx,
      endIndex:      endIdx,
      segmentCount:  count,
      lengthM,
      azimuth: segments[startIdx].azimuth,
      mid: {
        x: (startPoint.x + endPoint.x) / 2,
        y: (startPoint.y + endPoint.y) / 2,
      },
      normal: { x: dy / chordLen, y: -dx / chordLen },
    };
  };

  const groups = [];
  let start = 0;
  for (let i = 0; i < n; i++) {
    const isLast    = i === n - 1;
    const canExtend = !isLast &&
      angularDiffDeg(segments[i].azimuth, segments[i + 1].azimuth) <= toleranceDeg;
    if (!canExtend) { groups.push(buildGroup(start, i)); start = i + 1; }
  }

  // Fusionar primer y último lado si son colineales (para cerrar el polígono)
  if (
    groups.length > 1 &&
    angularDiffDeg(groups[0].azimuth, groups[groups.length - 1].azimuth) <= toleranceDeg
  ) {
    const first  = groups[0];
    const last   = groups[groups.length - 1];
    const merged = buildGroup(last.startIndex, first.endIndex);
    merged.segmentCount = last.segmentCount + first.segmentCount;
    merged.lengthM      = last.lengthM + first.lengthM;
    groups.splice(0, 1);
    groups[groups.length - 1] = merged;
  }

  return groups;
};

const applyOutwardNormals = (groups, points, isCCW) =>
  groups.map((group) => {
    const sp  = points[group.startIndex];
    const ep  = points[(group.endIndex + 1) % points.length];
    const dx  = ep.x - sp.x;
    const dy  = ep.y - sp.y;
    const len = Math.hypot(dx, dy) || 1;
    return {
      ...group,
      normal: {
        x: isCCW ?  dy / len : -dy / len,
        y: isCCW ? -dx / len :  dx / len,
      },
    };
  });

const simplifyClosedRing = (boundary, epsilonM, minVertices = 3) => {
  if (boundary.length <= minVertices || epsilonM <= 0) return boundary;
  const { points } = projectToLocalMeters(boundary);
  let indices = points.map((_, i) => i);

  let changed = true;
  while (changed && indices.length > minVertices) {
    changed = false;
    let removeAt = -1;
    let smallest = epsilonM;
    const nn = indices.length;
    for (let i = 0; i < nn; i++) {
      const prev = points[indices[(i - 1 + nn) % nn]];
      const curr = points[indices[i]];
      const next = points[indices[(i + 1) % nn]];
      const d    = perpendicularDistanceToSegment(curr, prev, next);
      if (d < smallest) { smallest = d; removeAt = i; }
    }
    if (removeAt >= 0) { indices.splice(removeAt, 1); changed = true; }
  }
  return indices.map((i) => boundary[i]);
};

/** Pipeline principal para la visualización de la planimetría del lote.  */
const buildDisplayGeometry = (cleanBoundary) => {
  const { points: rawPoints, origin } = projectToLocalMeters(cleanBoundary);
  const rawSegments  = buildSegmentsFromBoundary(cleanBoundary, rawPoints);
  const { areaM2, isCCW, perimeterM, bbox } = computePolygonMetrics(rawPoints, rawSegments);

  const collinearTolerance = getCollinearToleranceDeg(cleanBoundary.length, perimeterM);
  const targetMaxSides     = getTargetMaxSides(cleanBoundary.length, perimeterM);

  let displayBoundary  = cleanBoundary;
  let curveSimplified  = false;
  let collinearGroups  = groupCollinearSegments(rawSegments, rawPoints, collinearTolerance);

  if (collinearGroups.length > targetMaxSides || collinearGroups.length > MAX_VISIBLE_LABELS) {
    let epsilon    = getInitialSimplifyEpsilon(cleanBoundary.length, perimeterM);
    const maxEps   = Math.max(8, perimeterM / 40);
    let bestBoundary = cleanBoundary;
    let bestGroups   = collinearGroups;

    while (epsilon <= maxEps) {
      const candidate = simplifyClosedRing(cleanBoundary, epsilon, 3);
      if (candidate.length >= cleanBoundary.length) { epsilon *= 1.35; continue; }

      // Reutilizar el origin del boundary original para la proyección
      const { points: cPts } = projectToLocalMeters(candidate, origin);
      const cSegs   = buildSegmentsFromBoundary(candidate, cPts);
      const cGroups = groupCollinearSegments(cSegs, cPts, collinearTolerance);

      bestBoundary  = candidate;
      bestGroups    = cGroups;
      curveSimplified = true;

      if (cGroups.length <= targetMaxSides) break;
      epsilon *= 1.35;
    }

    displayBoundary = bestBoundary;
    collinearGroups = bestGroups;
  }

  const { points: displayPoints } = projectToLocalMeters(displayBoundary, origin);
  const displayGroups = applyOutwardNormals(collinearGroups, displayPoints, isCCW);

  // Etiquetas visibles
  const minLabelLength = Math.max(1.5, perimeterM * 0.012);
  let labeledGroups = displayGroups.map((g) => ({ ...g, showLabel: g.lengthM >= minLabelLength }));
  let visibleCount  = labeledGroups.filter((g) => g.showLabel).length;

  if (visibleCount > MAX_VISIBLE_LABELS) {
    const top = new Set(
      [...displayGroups].sort((a, b) => b.lengthM - a.lengthM)
        .slice(0, MAX_VISIBLE_LABELS).map((g) => g.startIndex),
    );
    labeledGroups = displayGroups.map((g) => ({ ...g, showLabel: top.has(g.startIndex) }));
  } else if (visibleCount < 3 && displayGroups.length >= 3) {
    const top = new Set(
      [...displayGroups].sort((a, b) => b.lengthM - a.lengthM)
        .slice(0, Math.min(6, displayGroups.length)).map((g) => g.startIndex),
    );
    labeledGroups = displayGroups.map((g) => ({ ...g, showLabel: top.has(g.startIndex) }));
  }

  return {
    rawPoints,
    displayPoints,
    displayBoundary,
    rawSegments,
    displayGroups:      labeledGroups,
    rawVertexCount:     cleanBoundary.length,
    displayVertexCount: displayBoundary.length,
    wasSimplified:      displayBoundary.length < cleanBoundary.length || displayGroups.length < rawSegments.length,
    curveSimplified,
    hiddenLabelCount:   labeledGroups.filter((g) => !g.showLabel).length,
    collinearTolerance,
    targetMaxSides,
    areaM2,
    perimeterM,
    bbox,
  };
};

// ─── sanitize boundary para cerrar el polígono ────────────────────────────────────

const sanitizeBoundary = (boundary) => {
  if (!Array.isArray(boundary)) return [];
  const pts = boundary
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return pts;
  const first = pts[0];
  const last  = pts[pts.length - 1];
  return (first.lat === last.lat && first.lng === last.lng) ? pts.slice(0, -1) : pts;
};

// ─── Subcomponentes (InfoMessage, SummaryRow) ────────────────────────────────────

const InfoMessage = ({ children }) => (
  <Box sx={{ px: 1.25, py: 1, borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.10)', bgcolor: 'rgba(0,0,0,0.12)' }}>
    <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.35 }}>
      {children}
    </Typography>
  </Box>
);

const SummaryRow = ({ label, value, hint = null, action = null, hideDivider = false }) => (
  <Box sx={lotInfoRowOuterSx(hideDivider)}>
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
      ...LOT_INFO_ROW_INNER_SX,
    }}>
    <Typography sx={lotInfoFieldLabelLayoutSx()}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textAlign: 'right' }}>
      <Box>
        <Typography sx={lotInfoFieldValueSx({ monospace: true })}>
          {value}
        </Typography>
        {hint && (
          <Typography sx={{ ...lotInfoFieldValueSx({ muted: true, monospace: true }) }}>
            {hint}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    </Box>
  </Box>
);

// ─── Componente principal ──────────────────────────────────────────────────────

const PADDING = 56;

const PlanimetriaSection = ({
  boundary,
  declaredSurfaceM2 = null,
  // frontAzimuth = null, // TODO: reactivar con isFrontSide
  lotShape = null,
  blocked = false,
  blockedMessage = 'Iniciá sesión para ver la planimetría.',
  hideTitle = false,
  hideLotShape = false,
}) => {
  const containerRef = useRef(null);
  const [width, setWidth]       = useState(0);

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const el = containerRef.current;
    let rafId;
    const ro = new ResizeObserver((entries) => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const w = entries[0]?.contentRect?.width ?? el.clientWidth;
        if (w > 0) setWidth(w);
      });
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => { ro.disconnect(); cancelAnimationFrame(rafId); };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const nextWidth = el.clientWidth;
    if (nextWidth > 0) setWidth(nextWidth);
  }, [boundary]);

  const cleanBoundary = useMemo(
    () => (blocked ? [] : sanitizeBoundary(boundary)),
    [boundary, blocked],
  );
  const geometry      = useMemo(
    () => cleanBoundary.length >= 3 ? buildDisplayGeometry(cleanBoundary) : null,
    [cleanBoundary],
  );


  /**
   * Todas las derivaciones del SVG en un único useMemo.
   * No se recalculan salvo que width o geometry cambien.
   */
  const svgLayout = useMemo(() => {
    if (!geometry || width <= 0) return null;

    const svgWidth  = Math.max(width, 240);
    const aspect    = geometry.bbox.heightM / geometry.bbox.widthM;
    const svgHeight = Math.max(220, Math.min(Math.round(svgWidth * Math.min(Math.max(aspect, 0.45), 1.25)), 380));
    const innerW    = svgWidth - PADDING * 2;
    const innerH    = svgHeight - PADDING * 2;
    const scale     = Math.min(innerW / geometry.bbox.widthM, innerH / geometry.bbox.heightM);
    const offsetX   = (svgWidth  - geometry.bbox.widthM  * scale) / 2;
    const offsetY   = (svgHeight - geometry.bbox.heightM * scale) / 2;

    const project = (p) => ({
      x: offsetX + (p.x - geometry.bbox.minX) * scale,
      y: svgHeight - (offsetY + (p.y - geometry.bbox.minY) * scale),
    });

    const projectedPoints = geometry.rawPoints.map(project);
    const polygonPath     = projectedPoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(' ') + ' Z';

    const scaleTargetPx = innerW * 0.3;
    const scaleStepM    = niceScaleStep(scaleTargetPx / scale);
    const scaleStepPx   = scaleStepM * scale;

    // Los datos de cada label (posición, ángulo, texto) se pre-computan
    // aquí, fuera del JSX, para que el render sea sólo una declaración.
    const visibleGroupsData = geometry.displayGroups
      .filter((g) => g.showLabel)
      .map((group, i) => {
        const midPx   = project(group.mid);
        const startPx = project(geometry.displayPoints[group.startIndex]);
        const endPx   = project(geometry.displayPoints[(group.endIndex + 1) % geometry.displayPoints.length]);
        const dx = endPx.x - startPx.x;
        const dy = endPx.y - startPx.y;
        const nxPx = -group.normal.x;
        const nyPx =  group.normal.y;
        const offset = 14;
        // const frontMatch = isFrontSide(group.azimuth, Number(frontAzimuth), 10);
        let angle = (Math.atan2(dy, dx) * 180) / Math.PI;
        if (angle >  90) angle -= 180;
        if (angle < -90) angle += 180;
        return {
          key:      `group-${i}`,
          label:    `${formatMetersAR(group.lengthM)} m`,
          tooltip:  `Lado ${i + 1}: ${formatMetersAR(group.lengthM)} m · ${formatBearingLabel(group.azimuth)}${group.segmentCount > 1 ? ` · ${group.segmentCount} tramos` : ''}`,
          midPx,
          startPx,
          endPx,
          nxPx, nyPx,
          offset,
          labelX:  midPx.x + nxPx * offset,
          labelY:  midPx.y + nyPx * offset,
          angle,
        };
      });

    return { svgWidth, svgHeight, polygonPath, scaleStepM, scaleStepPx, visibleGroupsData, project };
  }, [geometry, width]);

  // ─── Encabezado ──────────────────────────────────────────────────────────────
  const header = !hideTitle ? (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
      <Typography variant="caption" sx={SECTION_TITLE_SX}>Planimetría</Typography>
    </Box>
  ) : null;

  if (blocked) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {header}
        <InfoMessage>{blockedMessage}</InfoMessage>
      </Box>
    );
  }

  if (!Array.isArray(boundary) || boundary.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {header}
        <InfoMessage>No hay datos de perímetro disponibles para este lote.</InfoMessage>
      </Box>
    );
  }

  if (!geometry) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {header}
        <InfoMessage>El polígono recibido es inválido o no tiene suficientes vértices.</InfoMessage>
      </Box>
    );
  }


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {header}

      <Box
        ref={containerRef}
        sx={{ borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.10)', bgcolor: 'rgba(0,0,0,0.20)', overflow: 'hidden' }}
      >
        {svgLayout && (
          <svg
            width={svgLayout.svgWidth}
            height={svgLayout.svgHeight}
            viewBox={`0 0 ${svgLayout.svgWidth} ${svgLayout.svgHeight}`}
            style={{ display: 'block' }}
          >
            <defs>
              <pattern id="planim-grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke={GRID_COLOR} strokeWidth="1" />
              </pattern>
            </defs>

            <rect width={svgLayout.svgWidth} height={svgLayout.svgHeight} fill="url(#planim-grid)" />

            <path
              d={svgLayout.polygonPath}
              fill={FILL_COLOR}
              stroke={STROKE_COLOR}
              strokeWidth="1.5"
              strokeLinejoin="round"
            />

            {/* TODO: reactivar resaltado del frente del lote por azimuth
            {svgLayout.visibleGroupsData
              .filter(({ frontMatch }) => frontMatch)
              .map(({ key, startPx, endPx }) => (
                <line
                  key={`${key}-front`}
                  x1={startPx.x}
                  y1={startPx.y}
                  x2={endPx.x}
                  y2={endPx.y}
                  stroke="rgba(72,146,145,0.95)"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              ))}
            */}

            {/* Cotas — datos pre-calculados, render declarativo puro */}
            {svgLayout.visibleGroupsData.map(({ key, label, tooltip, midPx, nxPx, nyPx, offset, labelX, labelY, angle }) => (
              <Tooltip key={key} title={tooltip} placement="top" arrow>
                <g style={{ cursor: 'help' }}>
                  <line
                    x1={midPx.x} y1={midPx.y}
                    x2={midPx.x + nxPx * (offset - 4)}
                    y2={midPx.y + nyPx * (offset - 4)}
                    stroke="rgba(255,255,255,0.25)"
                    strokeWidth="1"
                  />
                  <text
                    x={labelX} y={labelY}
                    fill={LABEL_COLOR}
                    fontSize="10"
                    fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${angle.toFixed(2)} ${labelX.toFixed(2)} ${labelY.toFixed(2)})`}
                    style={{ paintOrder: 'stroke', stroke: 'rgba(0,0,0,0.55)', strokeWidth: 3 }}
                  >
                    {label}
                  </text>
                </g>
              </Tooltip>
            ))}

            {/* Brújula (Norte arriba REF)*/}
            <g transform={`translate(${svgLayout.svgWidth - 30} 30)`}>
              <circle r="16" fill="rgba(0,0,0,0.55)" stroke="rgba(255,255,255,0.25)" />
              <polygon points="0,-12 4,2 0,-2 -4,2" fill="#489291" stroke="rgba(0,0,0,0.6)" strokeWidth="0.5" />
              <polygon points="0,12 4,-2 0,2 -4,-2" fill="rgba(255,255,255,0.55)" stroke="rgba(0,0,0,0.6)" strokeWidth="0.5" />
              <text x="0" y="-18" fill="#489291" fontSize="9" fontWeight="700" textAnchor="middle" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">N</text>
            </g>

            {/* Escala gráfica */}
            <g transform={`translate(16 ${svgLayout.svgHeight - 18})`}>
              <line x1="0" y1="0" x2={svgLayout.scaleStepPx} y2="0" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" />
              <line x1="0" y1="-3" x2="0" y2="3" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" />
              <line x1={svgLayout.scaleStepPx} y1="-3" x2={svgLayout.scaleStepPx} y2="3" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" />
              <text x={svgLayout.scaleStepPx / 2} y="-6" fill="rgba(255,255,255,0.75)" fontSize="10" textAnchor="middle" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
                {`${formatMetersAR(svgLayout.scaleStepM, svgLayout.scaleStepM >= 10 ? 0 : 1)} m`}
              </text>
            </g>
          </svg>
        )}
      </Box>

      {/* Resumen */}
      <Box sx={{ borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.10)', bgcolor: 'rgba(0,0,0,0.14)', overflow: 'hidden' }}>
        <SummaryRow
          label="Perímetro"
          value={`${formatMetersAR(geometry.perimeterM)} m`}
          hideDivider={hideLotShape}
        />
        {hideLotShape ? null : (
          <SummaryRow label="Forma del lote" value={getLotShapeLabel(lotShape)} hideDivider />
        )}
      </Box>
    </Box>
  );
};

export default PlanimetriaSection;